"use client";

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowDown,
  ArrowUp,
  ChartLine,
  CurrencyDollar,
  Package,
  ShoppingCart,
  TrendUp,
  CreditCard,
  Receipt,
  Warning,
  CaretRight,
  CheckCircle,
  Clock,
  Users,
  Storefront,
} from "@phosphor-icons/react";
import { LogIn, Coins, Wallet, Undo2, AlertTriangle, CheckSquare } from "lucide-react";
import moment from "moment/moment";
import { motion } from "framer-motion";
import Loader from "@/components/custom/common/Loader";
import NoData from "@/components/custom/common/NoData";
import {
  fetchDashboardData,
  fetchSalesData,
  fetchInventoryData,
} from "@/state/analytics/analytic.slice";
import { getActivities } from "@/state/activity/activity.slice";
import { getShops } from "@/state/shops/shop.slice";
import { fetchUsers } from "@/state/users/users.slice";
import { getCategories } from "@/state/category/category.slice";

import QuickActions from "@/components/custom/dashboard/QuickActions";
import DashboardFilter from "@/components/custom/dashboard/DashboardFilter";
import PermissionGuard from "@/components/custom/common/PermissionGuard";
import { cn } from "@/lib/utils";
import { numberWithCommas } from "@/lib/formatCommas";

export const dynamic = "force-dynamic";

// Compact Summary Card Component
const SummaryCard = ({ title, value, subtitle, icon: Icon, iconBg, trend, trendDirection }) => (
  <Card className="bg-white dark:bg-slate-900 border shadow-sm">
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={cn("p-2", iconBg || "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400")}>
          <Icon className="h-5 w-5 text-blue-600" weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{title}</p>
          <p className="text-xl font-bold text-slate-900 dark:text-slate-100 truncate">{value}</p>
        </div>
        {trend && (
          <div className={cn(
            "flex items-center gap-1 text-xs font-medium px-2 py-1 rounded",
            trendDirection === "up" ? "bg-green-50 dark:bg-green-900/30 text-green-600" : "bg-red-50 dark:bg-red-900/30 text-red-600"
          )}>
            {trendDirection === "up" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
            {trend}
          </div>
        )}
      </div>
      {subtitle && <p className="text-[11px] text-slate-400 mt-1 ml-11">{subtitle}</p>}
    </CardContent>
  </Card>
);

// Category Card Component (for sidebar sections)
const CategoryCard = ({ title, value, icon: Icon, color, onClick }) => (
  <div
    className={cn(
      "flex items-center justify-between p-3 cursor-pointer transition-all hover:scale-[1.02]",
      color || "bg-blue-600 text-white"
    )}
    onClick={onClick}
  >
    <div className="flex items-center gap-3">
      <div className="p-2 bg-transparent dark:bg-slate-900/20">
        <Icon className="h-5 w-5" weight="fill" />
      </div>
      <div>
        <p className="text-sm font-medium opacity-90">{title}</p>
        <p className="text-2xl font-bold">{value}</p>
      </div>
    </div>
    <CaretRight className="h-5 w-5 opacity-60" />
  </div>
);

// Compact Activity List Item
const ActivityItem = ({ activity }) => {
  const getIcon = () => {
    switch (activity.type) {
      case "sale": return <ShoppingCart className="h-4 w-4 text-green-500" />;
      case "stock": return <Package className="h-4 w-4 text-blue-500" />;
      case "auth": return <LogIn className="h-4 w-4 text-purple-500" />;
      default: return <Clock className="h-4 w-4 text-slate-400" />;
    }
  };

  return (
    <div className="flex items-center gap-3 py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <div className="p-1.5 bg-slate-50 dark:bg-slate-800">
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700 dark:text-slate-300 truncate">{activity.description}</p>
        <p className="text-[11px] text-slate-400">{moment(activity.createdAt).fromNow()}</p>
      </div>
    </div>
  );
};

// Deadline/Task Item Component
const DeadlineItem = ({ title, date, done }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
    <div>
      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{title}</p>
      <p className="text-[11px] text-slate-400 flex items-center gap-1">
        <Clock className="h-3 w-3" />
        {date}
      </p>
    </div>
    <div className={cn(
      "w-5 h-5 rounded border-2 flex items-center justify-center",
      done ? "bg-green-50 dark:bg-green-900/30 border-green-500" : "border-slate-300"
    )}>
      {done && <CheckCircle className="h-4 w-4 text-white" weight="fill" />}
    </div>
  </div>
);

export default function AnalyticsDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  // Default to today only - user can expand with filter
  const [startDate, setStartDate] = useState(moment().startOf("day").toDate());
  const [endDate, setEndDate] = useState(moment().endOf("day").toDate());
  // New filter states
  const [shopFilter, setShopFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const dispatch = useDispatch();
  const { dashboardData, salesData, inventoryData, isLoading, error } = useSelector((state) => state.analytic);
  const { activities } = useSelector((state) => state.activity);
  const { shops } = useSelector((state) => state.shop || { shops: [] });
  const { users } = useSelector((state) => state.users || { users: [] });
  const { categories } = useSelector((state) => state.category || { categories: [] });

  const fetchData = (start, end, shop, user, category) => {
    const sDate = start || startDate;
    const eDate = end || endDate;
    const formattedStartDate = moment(sDate).format("YYYY-MM-DD");
    const formattedEndDate = moment(eDate).format("YYYY-MM-DD");
    dispatch(fetchDashboardData({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      shopId: shop !== undefined ? shop : shopFilter,
      userId: user !== undefined ? user : userFilter,
      categoryId: category !== undefined ? category : categoryFilter
    }));
  };

  const handleFilterChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    fetchData(start, end);
  };

  const handleShopChange = (val) => {
    setShopFilter(val);
    fetchData(startDate, endDate, val, userFilter, categoryFilter);
  };

  const handleUserChange = (val) => {
    setUserFilter(val);
    fetchData(startDate, endDate, shopFilter, val, categoryFilter);
  };

  const handleCategoryChange = (val) => {
    setCategoryFilter(val);
    fetchData(startDate, endDate, shopFilter, userFilter, val);
  };

  useEffect(() => {
    fetchData();
    dispatch(getShops());
    dispatch(fetchUsers());
    dispatch(getCategories());
  }, [dispatch]);

  useEffect(() => {
    const formattedStartDate = moment(startDate).format("YYYY-MM-DD");
    const formattedEndDate = moment(endDate).format("YYYY-MM-DD");
    dispatch(getActivities({ startDate: formattedStartDate, endDate: formattedEndDate, limit: 20 }));
  }, [startDate, endDate]);

  useEffect(() => {
    if (activeTab === "inventory" && !inventoryData) {
      dispatch(fetchInventoryData());
    }
  }, [activeTab, dispatch, inventoryData]);

  if (isLoading) return <Loader />;
  if (error) return <div>Error: {error}</div>;
  if (!dashboardData) return null;

  // Generate chart data for the bar chart visualization
  const chartData = dashboardData.salesData || [];

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="p-4 space-y-4 bg-slate-50 dark:bg-slate-800/50 min-h-screen">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3"
      >
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            Good {moment().hour() < 12 ? "Morning" : moment().hour() < 17 ? "Afternoon" : "Evening"}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {moment(startDate).isSame(moment(), 'day') && moment(endDate).isSame(moment(), 'day')
              ? "Today's Overview"
              : `Overview for ${moment(startDate).format("MMM D")} - ${moment(endDate).format("MMM D, YYYY")}`}
          </p>
        </div>
        <DashboardFilter
          startDate={startDate}
          endDate={endDate}
          onFilterChange={handleFilterChange}
          shops={shops || []}
          users={users || []}
          categories={categories || []}
          shopId={shopFilter}
          userId={userFilter}
          categoryId={categoryFilter}
          onShopChange={handleShopChange}
          onUserChange={handleUserChange}
          onCategoryChange={handleCategoryChange}
        />
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        <QuickActions />
      </motion.div>

      {/* Main Grid Layout */}
      <PermissionGuard required="ViewDashboardStats" fallback={
        <Card className="p-6 bg-white dark:bg-slate-900">
          <div className="text-center space-y-2">
            <h3 className="text-lg font-semibold">Welcome to Dashboard</h3>
            <p className="text-muted-foreground">You do not have permission to view detailed statistics.</p>
          </div>
        </Card>
      }>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 lg:grid-cols-4 gap-4"
        >

          {/* Left Column - Main Content (3 cols) */}
          <div className="lg:col-span-3 space-y-4">

            {/* Summary Cards Row */}
            {/* Financial Summary - Clear 2-Row Layout */}
            <motion.div variants={itemVariants}>
              <Card className="p-4 bg-white dark:bg-slate-900 border shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">Financial Summary</h3>

                {/* Row 1: Revenue Flow */}
                <div className="mb-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 font-medium">Revenue</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Total Revenue (Gross Sales) */}
                    <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 transition-colors hover:bg-blue-100/50">
                      <div className="p-2.5 bg-blue-100 dark:bg-blue-900">
                        <CurrencyDollar className="h-5 w-5 text-blue-600 dark:text-blue-400" weight="duotone" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Gross Revenue</p>
                        <p className="text-lg font-bold text-slate-900 dark:text-slate-100">₵{numberWithCommas((dashboardData.totalRevenue || 0).toFixed(2))}</p>
                        <p className="text-[10px] text-blue-600">{dashboardData.totalSales} sales</p>
                      </div>
                    </div>

                    {/* Returns/Discounts (Deductions) */}
                    <div className="flex items-center gap-3 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 transition-colors hover:bg-orange-100/50">
                      <div className="p-2.5 bg-orange-100 dark:bg-orange-900">
                        <Undo2 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Returns & Discounts</p>
                        <p className="text-lg font-bold text-orange-600">-₵{numberWithCommas((dashboardData.totalReturnsAmount || 0).toFixed(2))}</p>
                        <p className="text-[10px] text-orange-600">{dashboardData.returnedSales || 0} returns</p>
                      </div>
                    </div>

                    {/* Net Revenue */}
                    <div className="flex items-center gap-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 transition-colors hover:bg-indigo-100/50">
                      <div className="p-2.5 bg-indigo-100 dark:bg-indigo-900">
                        <Coins className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Net Revenue</p>
                        <p className="text-lg font-bold text-indigo-700 dark:text-indigo-300">₵{numberWithCommas((dashboardData.netRevenue || 0).toFixed(2))}</p>
                        <p className="text-[10px] text-slate-500">After returns</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 2: Profit Flow */}
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 font-medium">Profit</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Gross Profit */}
                    <div className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 transition-colors hover:bg-emerald-100/50">
                      <div className="p-2.5 bg-emerald-100 dark:bg-emerald-900">
                        <TrendUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" weight="duotone" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Gross Profit</p>
                        <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">₵{numberWithCommas((dashboardData.grossProfit || 0).toFixed(2))}</p>
                        <p className="text-[10px] text-emerald-600">Revenue - Cost</p>
                      </div>
                    </div>

                    {/* Expenses */}
                    <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 transition-colors hover:bg-red-100/50">
                      <div className="p-2.5 bg-red-100 dark:bg-red-900">
                        <Wallet className="h-5 w-5 text-red-500 dark:text-red-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Expenses</p>
                        <p className="text-lg font-bold text-red-600">-₵{numberWithCommas((dashboardData.totalExpenses || 0).toFixed(2))}</p>
                        <p className="text-[10px] text-red-500">Operating costs</p>
                      </div>
                    </div>

                    {/* Net Profit */}
                    <div className={cn(
                      "flex items-center gap-3 p-3 border transition-colors",
                      dashboardData.netProfit >= 0
                        ? "bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800 hover:bg-green-100/50"
                        : "bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800 hover:bg-red-100/50"
                    )}>
                      <div className={cn(
                        "p-2.5",
                        dashboardData.netProfit >= 0 ? "bg-green-100 dark:bg-green-900" : "bg-red-100 dark:bg-red-900"
                      )}>
                        <ChartLine className={cn(
                          "h-5 w-5",
                          dashboardData.netProfit >= 0 ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"
                        )} weight="duotone" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Net Profit</p>
                        <p className={cn(
                          "text-lg font-bold",
                          dashboardData.netProfit >= 0 ? "text-green-700 dark:text-green-300" : "text-red-600"
                        )}>
                          ₵{numberWithCommas((dashboardData.netProfit || 0).toFixed(2))}
                        </p>
                        <p className={cn(
                          "text-[10px] font-medium",
                          dashboardData.profitMargin >= 0 ? "text-green-600" : "text-red-500"
                        )}>
                          {dashboardData.profitMargin}% margin
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>


            {/* Operational Metrics Grid */}
            <motion.div variants={itemVariants} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <SummaryCard
                title="Outstanding"
                value={`₵${numberWithCommas((dashboardData.totalOutstanding || 0).toFixed(0))}`}
                subtitle={`${dashboardData.creditSales} credit sales`}
                icon={CreditCard}
                iconBg="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400"
              />
              <SummaryCard
                title="Payments Received"
                value={`₵${numberWithCommas((dashboardData.paymentsReceived || 0).toFixed(0))}`}
                subtitle="Cash & mobile"
                icon={Coins}
                iconBg="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
              />
              <SummaryCard
                title="Low Stock"
                value={numberWithCommas(dashboardData.lowStockItems || 0)}
                subtitle="Items below threshold"
                icon={AlertTriangle}
                iconBg="bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
              />
              <SummaryCard
                title="Total Products"
                value={numberWithCommas(dashboardData.totalProducts || 0)}
                subtitle="In catalog"
                icon={Package}
                iconBg="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400"
              />
            </motion.div>

            {/* Sales Chart Section */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white dark:bg-slate-900 border shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">Sales Trend</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        axisLine={{ stroke: '#e2e8f0' }}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        axisLine={{ stroke: '#e2e8f0' }}
                      />
                      <Tooltip
                        contentStyle={{
                          fontSize: 12,
                          borderRadius: 8,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}
                      />
                      <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex items-center gap-4 justify-center mt-2 text-xs text-slate-500 dark:text-slate-400">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 bg-blue-50 dark:bg-blue-900/30 rounded" />
                      <span>Sales Revenue</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>


            {/* Recent Activity Section */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white dark:bg-slate-900 border shadow-sm">
                <CardHeader className="py-3 border-b">
                  <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent className="p-0 max-h-[200px] overflow-y-auto">
                  {activities.length === 0 ? (
                    <div className="p-4">
                      <NoData message="No recent activities" />
                    </div>
                  ) : (
                    <div className="px-4">
                      {activities.slice(0, 6).map((activity) => (
                        <ActivityItem key={activity.id} activity={activity} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Right Column - Sidebar (1 col) */}
          <div className="space-y-4">

            {/* Inventory Categories */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white dark:bg-slate-900 border shadow-sm">
                <CardHeader className="py-3 border-b">
                  <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">Inventory Status</CardTitle>
                </CardHeader>
                <CardContent className="p-3 space-y-2">
                  <CategoryCard
                    title="Inventory Value"
                    value={`₵${numberWithCommas(dashboardData.inventoryValue.toFixed(0))}`}
                    icon={Package}
                    color="bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md shadow-blue-500/20"
                  />
                  <CategoryCard
                    title="Retail Value"
                    value={`₵${numberWithCommas(dashboardData.inventoryRetailValue.toFixed(0))}`}
                    icon={Storefront}
                    color="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-md shadow-emerald-500/20"
                  />
                </CardContent>
              </Card>
            </motion.div>

            {/* Alerts Section */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white dark:bg-slate-900 border shadow-sm">
                <CardHeader className="py-3 border-b">
                  <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <Warning className="h-4 w-4 text-orange-500" /> Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center justify-between p-2 bg-orange-50 dark:bg-orange-900/30 border border-orange-100 dark:border-orange-800 rounded-md">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">Low Stock Items</span>
                    </div>
                    <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                      {dashboardData.lowStockItems}
                    </Badge>
                  </div>

                  {dashboardData.totalOutstanding > 0 && (
                    <div className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 rounded-md">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-red-500" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">Outstanding Payments</span>
                      </div>
                      <Badge variant="secondary" className="bg-red-100 text-red-700">
                        {dashboardData.creditSales}
                      </Badge>
                    </div>
                  )}

                  {dashboardData.returnedSales > 0 && (
                    <div className="flex items-center justify-between p-2 bg-purple-50 dark:bg-purple-900/30 border border-purple-100 dark:border-purple-800 rounded-md">
                      <div className="flex items-center gap-2">
                        <Undo2 className="h-4 w-4 text-purple-500" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">Returned Items</span>
                      </div>
                      <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                        {dashboardData.returnedSales}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Quick Stats */}
            <motion.div variants={itemVariants}>
              <Card className="bg-white dark:bg-slate-900 border shadow-sm">
                <CardHeader className="py-3 border-b">
                  <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">Quick Stats</CardTitle>
                </CardHeader>
                <CardContent className="p-3">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Profit Margin</span>
                      <span className={cn(
                        "text-sm font-semibold",
                        dashboardData.profitMargin >= 0 ? "text-green-600" : "text-red-500"
                      )}>
                        {dashboardData.profitMargin}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Credit Sales</span>
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{dashboardData.creditSales}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Total Products</span>
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{dashboardData.totalProducts || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </PermissionGuard>
    </div>
  );
}
